import React from "react";

import { relativeTime } from "../utils/post";

// Listings are priced in UGX. The old version computed the number and then
// threw it away, returning the raw string, so a price never got its thousands
// separators.
const toUGX = (val) => {
  if (val == null || val === "") return null;
  const n = Number(val);
  if (Number.isNaN(n)) return String(val);
  return `${n.toLocaleString("en-US")} UGX`;
};

const sanitizeUrl = (u) => {
  if (!u) return null;
  if (/^https?:\/\//i.test(u)) return u;
  return "https://" + u.replace(/^\/+/, "");
};

// Ordered by how people here actually get in touch. The first channel a seller
// has becomes the card's one labelled button; the rest collapse to icons, so a
// listing with four channels does not present four equally loud choices.
const CHANNELS = [
  {
    key: "whatsapp",
    icon: "fab fa-whatsapp",
    label: "WhatsApp",
    href: (c) => `https://wa.me/${c.whatsapp}`,
    external: true,
  },
  {
    key: "phone",
    icon: "fas fa-phone",
    label: "Call",
    href: (c) => `tel:${c.phone}`,
  },
  {
    key: "email",
    icon: "fas fa-envelope",
    label: "Email",
    href: (c) => `mailto:${c.email}`,
  },
  {
    key: "website",
    icon: "fas fa-globe",
    label: "Website",
    href: (c) => sanitizeUrl(c.website),
    external: true,
  },
];

// Styling is in src/styles/bleep-theme.css under `.bl-product*`.
export default function ProductAd({ product }) {
  const {
    brand,
    category,
    description,
    address,
    price,
    contact,
    country,
    imageUrls,
    dateTime,
  } = product || {};

  const img =
    Array.isArray(imageUrls) && imageUrls.length > 0
      ? imageUrls[0]
      : "/images/placeholder-product.png";

  const channels = CHANNELS.filter((ch) => contact?.[ch.key]).map((ch) => ({
    ...ch,
    url: ch.href(contact),
  }));
  const [primary, ...secondary] = channels;

  const name = brand || "Genuine product";
  const where = [address, country].filter(Boolean).join(", ");
  const listed = dateTime ? relativeTime(dateTime) : "";

  return (
    <div className="col pb-3">
      <article className="bl-product">
        <div className="bl-product__media">
          <img
            className="bl-product__img"
            src={img}
            alt={name}
            loading="lazy"
          />
        </div>

        <div className="bl-product__body">
          {category && <div className="bl-product__eyebrow">{category}</div>}
          <h3 className="bl-product__title">{name}</h3>

          {/* Price is the number people came for, so it is a line of its own at
              a readable size rather than a 12px pill floating on the photo. An
              absent price is stated: a blank space reads as a loading bug. */}
          <div
            className={`bl-product__price${price ? "" : " bl-product__price--none"}`}
          >
            {toUGX(price) || "Contact for price"}
          </div>

          {description && <p className="bl-product__desc">{description}</p>}

          {(where || listed) && (
            <div className="bl-product__where">
              {where && (
                <span className="bl-product__where-place">
                  <i
                    className="fas fa-location-dot"
                    aria-hidden="true"
                  />
                  {where}
                </span>
              )}
              {listed && <span className="bl-product__age">{listed}</span>}
            </div>
          )}

          {primary && (
            <div className="bl-product__actions">
              <a
                href={primary.url}
                className="bl-product__cta"
                aria-label={`${primary.label} about ${name}`}
                {...(primary.external
                  ? { target: "_blank", rel: "noreferrer" }
                  : null)}
              >
                <i
                  className={primary.icon}
                  aria-hidden="true"
                />
                {primary.label}
              </a>

              {secondary.map((ch) => (
                <a
                  key={ch.key}
                  href={ch.url}
                  className="bl-product__action"
                  // Icon-only, so the name has to come from here. It carries
                  // the listing too: a grid of 16 cards would otherwise present
                  // sixteen links all called "Email".
                  aria-label={`${ch.label} about ${name}`}
                  title={ch.label}
                  {...(ch.external
                    ? { target: "_blank", rel: "noreferrer" }
                    : null)}
                >
                  <i
                    className={ch.icon}
                    aria-hidden="true"
                  />
                </a>
              ))}
            </div>
          )}
        </div>
      </article>
    </div>
  );
}
